import { useState, useCallback, useEffect, useRef } from 'react';
import { API_URL } from '../../../utils/api';

/**
 * Custom hook för att hämta och hantera all estimat-relaterad data
 * 
 * @param {string} leadId - ID för lead/procurement
 * @param {object} callbacks - Callbacks för state-uppdateringar
 * @returns {object} - State och funktioner för estimat-data
 */
// variantKey pekar ut vilket parallellt estimatspår som ska laddas.
// null/undefined = standardspåret, vilket är fallet för alla leads som inte
// har flera estimat.
const useEstimatData = (leadId, callbacks = {}, variantKey = null) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Använd useRef för callbacks för att undvika re-renders
  const callbacksRef = useRef(callbacks);
  
  // Uppdatera ref när callbacks ändras
  useEffect(() => {
    callbacksRef.current = callbacks;
  }, [callbacks]);

  /**
   * Hjälpfunktion för säker merge av objekt
   */
  const safeMerge = useCallback((defaultObj, newObj) => {
    if (!newObj) return defaultObj;
    if (!defaultObj) return newObj;
    
    const result = { ...defaultObj };
    Object.keys(newObj).forEach(key => {
      if (newObj[key] !== undefined && newObj[key] !== null) {
        if (typeof newObj[key] === 'object' && !Array.isArray(newObj[key]) && newObj[key] !== null) {
          // Säkerställ att defaultObj[key] finns som objekt
          const defaultValue = (defaultObj[key] && typeof defaultObj[key] === 'object') ? defaultObj[key] : {};
          result[key] = safeMerge(defaultValue, newObj[key]);
        } else {
          result[key] = newObj[key];
        }
      }
    });
    return result;
  }, []);

  /**
   * Formatera uppföljningsmöte-slot från estimatFollowup
   */
  const formatFollowupSlot = useCallback((followup) => {
    if (!followup) return { slot: '', url: '' };
    
    let slot = null;
    if (followup.meetingAt) {
      const date = new Date(followup.meetingAt);
      const weekdays = ['sön', 'mån', 'tis', 'ons', 'tors', 'fre', 'lör'];
      const weekday = weekdays[date.getUTCDay()];
      const day = date.getUTCDate();
      const month = date.getUTCMonth() + 1;
      const hours = String(date.getUTCHours()).padStart(2, '0');
      const minutes = String(date.getUTCMinutes()).padStart(2, '0');
      slot = `${weekday} ${day}/${month} kl ${hours}:${minutes}`;
    } else {
      slot = followup.selectedSlot || followup.displaySlot;
    }
    
    return {
      slot: slot || '',
      url: followup.teamsJoinUrl || ''
    };
  }, []);

  /**
   * Räkna badge-användningar för dynamiska anteckningar
   */
  const countBadgeUsage = useCallback((notes) => {
    const counts = {};
    const badges = ['Våtrum', 'Kök', 'Bygg', 'Måleri', 'Golv', 'El', 'VVS', 'Tillbyggnad', 'Källare', 'Utomhus'];
    
    notes.forEach(note => {
      badges.forEach(badge => {
        if (note.title.startsWith(badge)) {
          counts[badge] = (counts[badge] || 0) + 1;
        }
      });
    });
    
    return counts;
  }, []);

  /**
   * Huvudfunktion för att hämta all estimat-data
   */
  const refetchData = useCallback(async () => {
    if (!leadId) return;
    
    const cbs = callbacksRef.current;
    
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      
      // Backend middleware hanterar automatiskt lead/procurement mappning
      const estimatUrl = variantKey
        ? `${API_URL}/api/estimat/lead/${leadId}?variantKey=${encodeURIComponent(variantKey)}`
        : `${API_URL}/api/estimat/lead/${leadId}`;
      
      const estimatResponse = await fetch(estimatUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Backend svarar 200 { finns: false } när inget estimat finns (tidigare 404).
      // Båda fallen ska landa i "nytt estimat"-grenen nedan, annars blir vyn utan
      // lead/kund-data och visar "Kunde inte hämta projekt-data".
      const estimatBody = estimatResponse.ok ? await estimatResponse.json() : null;
      const harEstimat = !!(estimatBody && estimatBody._id && estimatBody.finns !== false);

      if (harEstimat) {
        const estimatData = estimatBody;

        // Extrahera procurement-data om den finns (från backend middleware)
        if (estimatData.procurementData && cbs.setProcurement) {
          cbs.setProcurement(estimatData.procurementData);
        }
        
        // Uppdatera metadata state
        if (cbs.setCurrentEstimatId) cbs.setCurrentEstimatId(estimatData._id);
        if (cbs.setCurrentVersion) cbs.setCurrentVersion(Number(estimatData.version || 1));
        if (cbs.setCurrentStatus) cbs.setCurrentStatus(estimatData.status || 'draft');
        if (cbs.setCurrentSentAt) cbs.setCurrentSentAt(estimatData.sentAt);
        if (cbs.setCustomerId) cbs.setCustomerId(estimatData.customerId);
        if (cbs.setCurrentProjectId) cbs.setCurrentProjectId(estimatData.projectId);
        if (cbs.setApprovedByConsultant) cbs.setApprovedByConsultant(estimatData.approvedByConsultant || false);
        if (cbs.setShowEstimatToCustomer) cbs.setShowEstimatToCustomer(estimatData.showEstimatToCustomer || false);
        if (cbs.setShowForstudiePresentationToCustomer) // ?? och inte || — äldre estimat saknar fältet och ska då följa
          // modellens default (true), inte tolkas som urkryssat.
          cbs.setShowForstudiePresentationToCustomer(estimatData.showForstudiePresentationToCustomer ?? true);
        // Äldre estimat saknar fältet — då gäller svenska
        if (cbs.setForstudiePresentationSprak)
          cbs.setForstudiePresentationSprak(estimatData.forstudiePresentationSprak || 'sv');
        if (cbs.setVisaTidigareVersionerForKund)
          cbs.setVisaTidigareVersionerForKund(estimatData.visaTidigareVersionerForKund === true);
        if (cbs.setBaspaketAndelar)
          cbs.setBaspaketAndelar(Array.isArray(estimatData.baspaketAndelar) ? estimatData.baspaketAndelar : null);
        // Registrerad beställare 2 = en extrakund som inte är deltagare.
        // Deltagare ligger i samma lista men är inte beställare.
        if (cbs.setBestallare2Registered) {
          const idAv = (x) => String(x?._id || x || '');
          const deltagare = new Set((estimatData.deltagareIds || []).map(idAv));
          cbs.setBestallare2Registered(
            (estimatData.additionalCustomerIds || []).some(x => x && !deltagare.has(idAv(x)))
          );
        }

        // Sätt uppföljningsinfo
        if (estimatData.estimatFollowup && cbs.setEstimatFollowup) {
          cbs.setEstimatFollowup(estimatData.estimatFollowup);
          
          const { slot, url } = formatFollowupSlot(estimatData.estimatFollowup);
          if (cbs.setBookedFollowupSlot) cbs.setBookedFollowupSlot(slot);
          if (cbs.setFollowupTeamsUrl) cbs.setFollowupTeamsUrl(url);
        }
        
        // Hämta kundaktivitetsdata om estimatet är skickat
        if (estimatData.sentAt && estimatData._id && cbs.fetchCustomerActivity) {
          cbs.fetchCustomerActivity(estimatData._id);
          if (cbs.setShowingAllActivities) cbs.setShowingAllActivities(false);
        }
        
        // Uppdatera formulärdata med säker merge
        if (estimatData.beställare2 || estimatData.projectInfo || estimatData.intervjuFrågor || estimatData.estimatKategorier) {
          if (cbs.setFormData) {
            cbs.setFormData(prev => {
              // Säkerställ att beställare2 alltid har korrekta strängvärden
              const mergedBeställare2 = safeMerge(prev.beställare2, estimatData.beställare2);
              const cleanBeställare2 = {
                firstName: mergedBeställare2?.firstName || '',
                lastName: mergedBeställare2?.lastName || '',
                phone: mergedBeställare2?.phone || '',
                email: mergedBeställare2?.email || ''
              };

              // Säkerställ att projectInfo alltid har korrekta värden
              const mergedProjectInfo = safeMerge(prev.projectInfo, estimatData.projectInfo);
              const cleanProjectInfo = {
                // Behåll alla fält från merged (inkl. etableringPercent!)
                ...mergedProjectInfo,
                // Override med säkra defaultvärden för kritiska fält
                budget: mergedProjectInfo?.budget || '',
                finansiering: mergedProjectInfo?.finansiering || '',
                rotAvdrag: mergedProjectInfo?.rotAvdrag ?? 0,
                rotBelopp: mergedProjectInfo?.rotBelopp ?? 0,
                önskadStart: mergedProjectInfo?.önskadStart || '',
                workDays: mergedProjectInfo?.workDays || null,
                // VIKTIGT: Bevara etableringPercent (default 10 om inte satt)
                etableringPercent: mergedProjectInfo?.etableringPercent ?? 10
              };

              const result = {
                ...prev,
                beställare2: cleanBeställare2,
                projectInfo: cleanProjectInfo,
                intervjuFrågor: safeMerge(prev.intervjuFrågor, estimatData.intervjuFrågor),
                // KRITISKT: Backend skickar 'estimatKategorier' men frontend använder 'estimat'
                estimat: estimatData.estimatKategorier || prev.estimat
              };
              // Spara original estimatKategorier för versionskontroll
              if (estimatData.estimatKategorier && cbs.setOriginalEstimatKategorier) {
                cbs.setOriginalEstimatKategorier(JSON.parse(JSON.stringify(estimatData.estimatKategorier)));
              }
              
              return result;
            });
          }
        }

        // Ladda dynamiska anteckningar separat
        if (estimatData.intervjuFrågor?.dynamicNotes) {
          if (cbs.setDynamicNotes) cbs.setDynamicNotes(estimatData.intervjuFrågor.dynamicNotes);
          
          const counts = countBadgeUsage(estimatData.intervjuFrågor.dynamicNotes);
          if (cbs.setBadgeCounts) cbs.setBadgeCounts(counts);
        } else {
          if (cbs.setDynamicNotes) cbs.setDynamicNotes([]);
          if (cbs.setBadgeCounts) cbs.setBadgeCounts({});
        }

        // Ladda rumsmått separat
        if (estimatData.roomMeasurements) {
          if (cbs.setRoomMeasurements) cbs.setRoomMeasurements(estimatData.roomMeasurements);
        } else {
          if (cbs.setRoomMeasurements) cbs.setRoomMeasurements([]);
        }

        // Ladda customer response och kommentarer
        if (estimatData.customerResponse) {
          if (cbs.setCustomerResponse) cbs.setCustomerResponse(estimatData.customerResponse);
          if (cbs.setCustomerComments) cbs.setCustomerComments(estimatData.customerResponse.comments || {});
        } else {
          if (cbs.setCustomerResponse) cbs.setCustomerResponse(null);
          if (cbs.setCustomerComments) cbs.setCustomerComments({});
        }

        // Ladda byggkonsult responses
        if (estimatData.byggkonsultResponses) {
          if (cbs.setByggkonsultResponses) cbs.setByggkonsultResponses(estimatData.byggkonsultResponses);
        } else {
          if (cbs.setByggkonsultResponses) cbs.setByggkonsultResponses({});
        }

        // Ladda byggkonsultens tidsförslag från estimatFollowup
        if (estimatData.estimatFollowup && estimatData.estimatFollowup.proposals) {
          if (cbs.setBuilderProposals) cbs.setBuilderProposals(estimatData.estimatFollowup);
        } else {
          if (cbs.setBuilderProposals) cbs.setBuilderProposals(null);
        }

        // Hämta Lead-data för estimat om leadId finns
        if (estimatData.leadId) {
          try {
            const leadResponse = await fetch(`${API_URL}/api/leads/${estimatData.leadId}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (leadResponse.ok) {
              const leadData = await leadResponse.json();
              if (cbs.setLead) cbs.setLead(leadData);
            } else {
              console.warn('⚠️ Kunde inte hämta Lead-data för befintligt estimat');
            }
          } catch (error) {
            console.error('❌ Fel vid hämtning av Lead-data för befintligt estimat:', error);
          }
        } else if (estimatData.procurementData || cbs.procurement) {
          // För procurement - använd procurement/estimat-data som innehåller allt vi behöver
          const fetchedProcurement = estimatData.procurementData || cbs.procurement;

          // Skapa ett mock lead-objekt från estimat-data/procurement-data för UI-kompatibilitet
          const ciFirst = fetchedProcurement?.customerInfo?.firstName || estimatData.customerInfo?.firstName || '';
          const ciLast = fetchedProcurement?.customerInfo?.lastName || estimatData.customerInfo?.lastName || '';
          const ciFullName = `${ciFirst} ${ciLast}`.trim();
          const mockLead = {
            _id: estimatData.leadId || leadId,
            projectInfo: fetchedProcurement?.projectInfo || estimatData.projectInfo,
            customerInfo: {
              firstName: ciFirst,
              lastName: ciLast,
              // Föredra firstName+lastName framför det gamla `name`-fältet, som kan spreta
              // efter ett beställare-byte (beställare 1 → beställare 2) och visa fel person.
              name: ciFullName
                || fetchedProcurement?.customerInfo?.name
                || estimatData.customerInfo?.name
                || 'Okänd kund'
            }
          };
          
          if (cbs.setLead) cbs.setLead(mockLead);
        }
      } else if (estimatResponse.ok || estimatResponse.status === 404) {
        // Inget estimat finns än - sätt default värden (tomt utkast)
        const procurementData = estimatBody?.procurementData || cbs.procurement || null;
        if (procurementData && cbs.setProcurement) cbs.setProcurement(procurementData);

        if (cbs.setCurrentEstimatId) cbs.setCurrentEstimatId(null);
        if (cbs.setCurrentVersion) cbs.setCurrentVersion(1);
        if (cbs.setCurrentStatus) cbs.setCurrentStatus('draft');
        if (cbs.setCurrentSentAt) cbs.setCurrentSentAt(null);
        if (cbs.setCustomerId) cbs.setCustomerId(null);
        if (cbs.setCurrentProjectId) cbs.setCurrentProjectId(null);
        if (cbs.setApprovedByConsultant) cbs.setApprovedByConsultant(false);
        if (cbs.setShowEstimatToCustomer) cbs.setShowEstimatToCustomer(false);
        
        // Sätt tom formulärdata
        if (cbs.setFormData) {
          cbs.setFormData(prev => ({
            ...prev,
            beställare2: {
              firstName: '',
              lastName: '',
              phone: '',
              email: ''
            },
            projectInfo: {
              budget: '',
              finansiering: '',
              rotAvdrag: 0,
              rotBelopp: 0,
              önskadStart: '',
              workDays: null
            },
            intervjuFrågor: {},
            estimatKategorier: {}
          }));
        }

        // Hämta Lead-data separat för nya estimat. URL:en kan innehålla ett
        // procurement-id (t.ex. "Gå till förstudie" i upphandlingen), så vi
        // använder i första hand procurementens leadId.
        let leadHamtad = false;
        const leadIdKandidat = (typeof procurementData?.leadId === 'object'
          ? procurementData?.leadId?._id
          : procurementData?.leadId) || leadId;

        try {
          const leadResponse = await fetch(`${API_URL}/api/leads/${leadIdKandidat}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });

          if (leadResponse.ok) {
            const leadData = await leadResponse.json();
            if (cbs.setLead) cbs.setLead(leadData);
            leadHamtad = true;
          }
        } catch (error) {
          console.error('❌ Kunde inte hämta lead-data:', error);
        }

        // Ingen Lead (t.ex. upphandling skapad utan förstudie) — bygg ett
        // mock-lead ur procurementen så vyn kan visa kund och projektinfo
        // i stället för "Kunde inte hämta projekt-data".
        if (!leadHamtad && procurementData && cbs.setLead) {
          const ciFirst = procurementData?.customerInfo?.firstName || '';
          const ciLast = procurementData?.customerInfo?.lastName || '';
          const ciFullName = `${ciFirst} ${ciLast}`.trim();
          cbs.setLead({
            _id: leadIdKandidat,
            projectInfo: procurementData?.projectInfo || {},
            customerInfo: {
              firstName: ciFirst,
              lastName: ciLast,
              name: ciFullName || procurementData?.customerInfo?.name || 'Okänd kund'
            }
          });
        }
      } else {
        throw new Error(`HTTP ${estimatResponse.status}: ${estimatResponse.statusText}`);
      }
    } catch (error) {
      console.error('❌ Fel vid omladdning av estimat-data:', error);
      setError(error);
    } finally {
      setLoading(false);
    }
  }, [leadId, variantKey, safeMerge, formatFollowupSlot, countBadgeUsage]);

  // Ladda estimat-data vid mount och när leadId ändras
  // VIKTIGT: Lyssna bara på leadId, inte refetchData, för att undvika infinite loop
  useEffect(() => {
    refetchData();
  }, [leadId, variantKey, refetchData]);

  return {
    loading,
    error,
    refetchData
  };
};

export default useEstimatData;
